/**
 * Keychron GWP — config-driven cart gift picker.
 * Inactive unless shop.metafields.custom.gwp_campaign is valid and in window.
 */
(() => {
  if (window.__kcGwpBootstrapped) return;
  window.__kcGwpBootstrapped = true;

  const CONFIG_EL_ID = 'kc-gwp-config';
  const CONTEXT_EL_ID = 'kc-gwp-context';
  const PROPERTY_KEY_DEFAULT = '_kc_gwp';

  function readRawConfig() {
    const el = document.getElementById(CONFIG_EL_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[kc-gwp] invalid config JSON', e);
      return null;
    }
  }

  function readContext() {
    const el = document.getElementById(CONTEXT_EL_ID);
    if (!el) return { countryCode: '' };
    try {
      const ctx = JSON.parse(el.textContent);
      return {
        countryCode: typeof ctx.countryCode === 'string' ? ctx.countryCode.toUpperCase() : ''
      };
    } catch (e) {
      return { countryCode: '' };
    }
  }

  function normalizeConfig(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.version !== 1) return null;
    if (!raw.campaign_id || typeof raw.campaign_id !== 'string') return null;
    if (!raw.starts_at || !raw.ends_at) return null;

    const starts = Date.parse(raw.starts_at);
    const ends = Date.parse(raw.ends_at);
    if (Number.isNaN(starts) || Number.isNaN(ends) || starts >= ends) return null;

    const now = Date.now();
    if (now < starts || now > ends) return null;

    const prefixes = Array.isArray(raw.eligible_sku_prefixes)
      ? raw.eligible_sku_prefixes.filter((p) => typeof p === 'string' && p.length > 0)
      : [];
    const productIds = Array.isArray(raw.eligible_product_ids)
      ? raw.eligible_product_ids.map((id) => Number(id)).filter((id) => id > 0)
      : [];
    const minEligibleSubtotal = Number(raw.min_eligible_subtotal);
    const thresholdMode =
      Number.isFinite(minEligibleSubtotal) && minEligibleSubtotal > 0;
    if (!thresholdMode && prefixes.length === 0 && productIds.length === 0) return null;

    const excludedVariantIds = new Set(
      Array.isArray(raw.excluded_variant_ids)
        ? raw.excluded_variant_ids.map((id) => Number(id)).filter((id) => id > 0)
        : []
    );

    const countryCodes = Array.isArray(raw.eligible_country_codes)
      ? raw.eligible_country_codes
          .filter((c) => typeof c === 'string' && c.length === 2)
          .map((c) => c.toUpperCase())
      : [];

    const gifts = Array.isArray(raw.gifts)
      ? raw.gifts
          .map((g) => ({
            variant_id: Number(g.variant_id),
            product_handle: typeof g.product_handle === 'string' ? g.product_handle : '',
            title: typeof g.title === 'string' ? g.title : ''
          }))
          .filter((g) => g.variant_id > 0 && g.title)
      : [];
    if (gifts.length === 0) return null;

    const defaultVariantId = Number(raw.default_variant_id);
    if (!defaultVariantId || !gifts.some((g) => g.variant_id === defaultVariantId)) return null;

    const maxPerOrder = Number(raw.max_per_order);
    if (maxPerOrder !== 1) return null;

    // Country gate: if list is non-empty, current localization must match
    if (countryCodes.length > 0) {
      const { countryCode } = readContext();
      if (!countryCode || !countryCodes.includes(countryCode)) return null;
    }

    return {
      campaignId: raw.campaign_id,
      prefixes,
      productIds,
      thresholdMode,
      minEligibleSubtotalCents: thresholdMode ? Math.round(minEligibleSubtotal * 100) : null,
      excludedVariantIds,
      countryCodes,
      gifts,
      defaultVariantId,
      maxPerOrder,
      propertyKey:
        typeof raw.line_property_key === 'string' && raw.line_property_key
          ? raw.line_property_key
          : PROPERTY_KEY_DEFAULT
    };
  }

  function money(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const fmt =
        (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) ||
        (window.theme && theme.moneyFormat) ||
        '${{amount}}';
      return Shopify.formatMoney(cents, fmt);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  function routes() {
    const r = (window.theme && theme.routes) || {};
    return {
      cart: `${r.cart || '/cart'}.js`,
      cartAdd: r.cartAdd || '/cart/add.js',
      cartChange: r.cartChange || '/cart/change.js'
    };
  }

  async function fetchCart() {
    const res = await fetch(routes().cart, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('cart_fetch_failed');
    return res.json();
  }

  function lineProps(item) {
    return item.properties || {};
  }

  function isEligibleLine(item, config) {
    const sku = item.sku || '';
    if (config.prefixes.some((p) => sku.startsWith(p))) return true;
    if (config.productIds.includes(Number(item.product_id))) return true;
    return false;
  }

  function isGwpLine(item, config) {
    const props = lineProps(item);
    return props[config.propertyKey] === config.campaignId;
  }

  function giftVariantIds(config) {
    return new Set(config.gifts.map((g) => g.variant_id));
  }

  function isCountableForThreshold(item, config) {
    const variantId = Number(item.variant_id);
    if (!variantId) return false;
    if (isGwpLine(item, config)) return false;
    if (giftVariantIds(config).has(variantId)) return false;
    if (config.excludedVariantIds && config.excludedVariantIds.has(variantId)) return false;
    return true;
  }

  function eligibleSubtotalCents(cart, config) {
    return (cart.items || []).reduce((sum, item) => {
      if (!isCountableForThreshold(item, config)) return sum;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
  }

  function cartIsEligible(cart, config) {
    if (config.thresholdMode) {
      return eligibleSubtotalCents(cart, config) >= config.minEligibleSubtotalCents;
    }
    return (cart.items || []).some((item) => isEligibleLine(item, config));
  }

  function findSelectedGiftVariantId(cart, config) {
    const gwp = (cart.items || []).find((item) => isGwpLine(item, config));
    if (gwp) return Number(gwp.variant_id);
    const ids = giftVariantIds(config);
    const manual = (cart.items || []).find((item) => ids.has(Number(item.variant_id)));
    return manual ? Number(manual.variant_id) : null;
  }

  async function cartAdd(variantId, config) {
    const res = await fetch(routes().cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [config.propertyKey]: config.campaignId
            }
          }
        ]
      })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'cart_add_failed');
    return data;
  }

  async function cartChangeLine(line, quantity) {
    const res = await fetch(routes().cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.errors || data.message || 'cart_change_failed');
    return data;
  }

  async function removeGwpLines(cart, config) {
    const lines = [];
    (cart.items || []).forEach((item, index) => {
      if (isGwpLine(item, config)) lines.push(index + 1);
    });
    // Remove from end so indexes stay valid
    for (let i = lines.length - 1; i >= 0; i -= 1) {
      await cartChangeLine(lines[i], 0);
    }
  }

  async function ensureSingleGift(cart, config, variantId) {
    const ids = giftVariantIds(config);
    let items = cart.items || [];

    // Already has this variant (gwp or manual) — do not add a second
    const existing = items.find((item) => Number(item.variant_id) === variantId);
    if (existing) {
      const extras = [];
      items.forEach((item, index) => {
        if (isGwpLine(item, config) && Number(item.variant_id) !== variantId) {
          extras.push(index + 1);
        }
      });
      for (let i = extras.length - 1; i >= 0; i -= 1) {
        await cartChangeLine(extras[i], 0);
      }
      return;
    }

    // Remove existing gwp-marked lines, then re-read cart before further checks
    await removeGwpLines(cart, config);
    cart = await fetchCart();
    items = cart.items || [];

    // Another gift SKU already in cart (e.g. manual add) — do not stack a second mouse
    const otherGift = items.find(
      (item) => ids.has(Number(item.variant_id)) && Number(item.variant_id) !== variantId
    );
    if (otherGift) {
      return;
    }

    // Target may already be present after removals
    if (items.some((item) => Number(item.variant_id) === variantId)) {
      return;
    }

    await cartAdd(variantId, config);
  }

  async function refreshCartUi() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      await cartItems.refresh();
      return;
    }
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }

  async function loadGiftAvailability(config) {
    const out = new Map();
    await Promise.all(
      config.gifts.map(async (gift) => {
        if (!gift.product_handle) {
          out.set(gift.variant_id, { available: true, price: null, image: null, title: gift.title });
          return;
        }
        try {
          const res = await fetch(`/products/${gift.product_handle}.js`, {
            credentials: 'same-origin',
            cache: 'no-store'
          });
          if (!res.ok) {
            out.set(gift.variant_id, { available: false, price: null, image: null, title: gift.title });
            return;
          }
          const product = await res.json();
          const variant = (product.variants || []).find((v) => Number(v.id) === gift.variant_id);
          out.set(gift.variant_id, {
            available: Boolean(variant && variant.available),
            price: variant ? variant.price : null,
            compare_at_price: variant ? variant.compare_at_price : null,
            image: (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || null,
            title: gift.title || product.title
          });
        } catch (e) {
          out.set(gift.variant_id, { available: false, price: null, image: null, title: gift.title });
        }
      })
    );
    return out;
  }

  class KcGwpPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.listEl = this.querySelector('.kc-gwp__list');
      this.statusEl = this.querySelector('.kc-gwp__status');
      this.hintEl = this.querySelector('.kc-gwp__hint');
      this._syncing = false;

      this.onCartEvent = () => {
        this.scheduleSync();
      };

      document.addEventListener('on:cart:add', this.onCartEvent);
      document.addEventListener('on:line-item:change', this.onCartEvent);
      document.addEventListener('on:cart-drawer:after-open', this.onCartEvent);
      document.addEventListener('dispatch:cart-drawer:refresh', this.onCartEvent);

      this.bindCheckoutGuards();
      this.scheduleSync();
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.onCartEvent);
      document.removeEventListener('on:line-item:change', this.onCartEvent);
      document.removeEventListener('on:cart-drawer:after-open', this.onCartEvent);
      document.removeEventListener('dispatch:cart-drawer:refresh', this.onCartEvent);
    }

    scheduleSync() {
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => {
        this.sync().catch((e) => console.warn('[kc-gwp]', e));
      }, 50);
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;
      if (!message) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        this.statusEl.classList.remove('is-error');
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('is-error', Boolean(isError));
    }

    bindCheckoutGuards() {
      if (window.__kcGwpCheckoutGuardBound) return;
      window.__kcGwpCheckoutGuardBound = true;

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (form.id !== 'cart-drawer-form' && form.id !== 'cart') return;
          if (form.dataset.kcGwpCheckoutReady === '1') return;

          const submitter = event.submitter;
          if (submitter && submitter.getAttribute('name') && submitter.getAttribute('name') !== 'checkout') {
            return;
          }
          const isCheckout =
            !submitter ||
            submitter.getAttribute('name') === 'checkout' ||
            Boolean(form.querySelector('[name="checkout"]'));
          if (!isCheckout) return;

          const config = normalizeConfig(readRawConfig());
          if (!config) return;

          const picker = document.querySelector('kc-gwp-picker');
          event.preventDefault();
          event.stopPropagation();

          const run =
            picker && typeof picker.ensureGiftThenCheckout === 'function'
              ? picker.ensureGiftThenCheckout(form, config, submitter)
              : Promise.resolve().then(() => {
                  window.location.href = '/checkout';
                });

          run.catch((err) => {
            console.warn('[kc-gwp] checkout guard', err);
            if (picker && typeof picker.setStatus === 'function') {
              picker.setStatus(picker.dataset.error || 'Unable to add free gift. Please try again.', true);
            }
          });
        },
        true
      );
    }

    async ensureGiftThenCheckout(form, config, submitter) {
      this.setStatus(this.dataset.loading || 'Updating…', false);
      let cart = await fetchCart();

      if (!cartIsEligible(cart, config)) {
        await removeGwpLines(cart, config);
        this.submitCheckout(form, submitter);
        return;
      }

      const selected = findSelectedGiftVariantId(cart, config);
      const availability = await loadGiftAvailability(config);
      const availableGifts = config.gifts.filter((g) => {
        const info = availability.get(g.variant_id);
        return info && info.available;
      });

      if (availableGifts.length === 0) {
        // Do not leave an unsellable $0 gift in cart — must not block paid checkout
        await removeGwpLines(cart, config);
        this.submitCheckout(form, submitter);
        return;
      }

      const targetId = selected || config.defaultVariantId;
      const targetOk = availableGifts.some((g) => g.variant_id === targetId);
      const finalId = targetOk ? targetId : availableGifts[0].variant_id;

      cart = await fetchCart();
      await ensureSingleGift(cart, config, finalId);
      await refreshCartUi();
      this.submitCheckout(form, submitter);
    }

    submitCheckout(form, submitter) {
      form.dataset.kcGwpCheckoutReady = '1';
      // Prefer /checkout after cart is correct — avoids losing name=checkout on form.submit()
      window.location.href = '/checkout';
    }

    async sync() {
      if (this._syncing) return;
      const config = normalizeConfig(readRawConfig());
      if (!config) {
        this.hidden = true;
        return;
      }

      this._syncing = true;
      try {
        let cart = await fetchCart();
        const eligible = cartIsEligible(cart, config);

        if (!eligible) {
          const gwpLines = (cart.items || []).filter((item) => isGwpLine(item, config));
          if (gwpLines.length) {
            await removeGwpLines(cart, config);
            await refreshCartUi();
            cart = await fetchCart();
          }
          this.hidden = true;
          this.setStatus('', false);
          return;
        }

        const availability = await loadGiftAvailability(config);
        const availableGifts = config.gifts.filter((g) => {
          const info = availability.get(g.variant_id);
          return info && info.available;
        });

        // Remove cart gift if its variant is no longer sellable
        const selectedId = findSelectedGiftVariantId(cart, config);
        if (selectedId) {
          const selectedInfo = availability.get(selectedId);
          if (!selectedInfo || !selectedInfo.available) {
            await removeGwpLines(cart, config);
            await refreshCartUi();
            cart = await fetchCart();
          }
        }

        if (availableGifts.length === 0) {
          const leftover = (cart.items || []).filter((item) => isGwpLine(item, config));
          if (leftover.length) {
            await removeGwpLines(cart, config);
            await refreshCartUi();
          }
          if (this.listEl) this.listEl.innerHTML = '';
          this.hidden = false;
          this.setStatus(
            this.dataset.soldOut ||
              'Our gift has sold out. Your items are still available and can be purchased as normal.',
            false
          );
          return;
        }

        // Cap: too many gwp lines
        const gwpOnly = (cart.items || []).filter((item) => isGwpLine(item, config));
        if (gwpOnly.length > config.maxPerOrder) {
          for (let i = gwpOnly.length - 1; i >= config.maxPerOrder; i -= 1) {
            const lineIndex = cart.items.indexOf(gwpOnly[i]) + 1;
            await cartChangeLine(lineIndex, 0);
          }
          await refreshCartUi();
          cart = await fetchCart();
        }

        const currentSelectedId = findSelectedGiftVariantId(cart, config);
        this.renderOptions(config, availability, availableGifts, currentSelectedId);
        this.hidden = false;
        this.setStatus('', false);
      } finally {
        this._syncing = false;
      }
    }

    renderOptions(config, availability, availableGifts, selectedId) {
      if (!this.listEl) return;
      this.listEl.innerHTML = '';

      availableGifts.forEach((gift) => {
        const info = availability.get(gift.variant_id) || {};
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kc-gwp__option';
        if (selectedId === gift.variant_id) btn.classList.add('is-selected');
        btn.dataset.variantId = String(gift.variant_id);

        const media = document.createElement('div');
        media.className = 'kc-gwp__media';
        if (info.image) {
          const img = document.createElement('img');
          img.src = info.image.startsWith('//') ? `https:${info.image}` : info.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 48;
          img.height = 48;
          media.appendChild(img);
        }

        const meta = document.createElement('div');
        meta.className = 'kc-gwp__meta';
        const title = document.createElement('span');
        title.className = 'kc-gwp__title';
        title.textContent = info.title || gift.title;
        meta.appendChild(title);

        const price = document.createElement('div');
        price.className = 'kc-gwp__price';
        const now = document.createElement('span');
        now.className = 'kc-gwp__price-now';
        now.textContent = money(0);
        price.appendChild(now);
        if (info.price != null && Number(info.price) > 0) {
          const was = document.createElement('span');
          was.className = 'kc-gwp__price-was';
          was.textContent = money(info.price);
          price.appendChild(was);
        }
        meta.appendChild(price);

        const action = document.createElement('span');
        action.className = 'kc-gwp__action';
        action.textContent =
          selectedId === gift.variant_id
            ? this.dataset.selected || 'Selected'
            : this.dataset.select || 'Select';

        btn.appendChild(media);
        btn.appendChild(meta);
        btn.appendChild(action);
        btn.addEventListener('click', () => {
          this.chooseGift(config, gift.variant_id);
        });

        li.appendChild(btn);
        this.listEl.appendChild(li);
      });
    }

    async chooseGift(config, variantId) {
      if (this._syncing) return;
      this._syncing = true;
      this.setStatus(this.dataset.loading || 'Updating…', false);
      try {
        const cart = await fetchCart();
        await ensureSingleGift(cart, config, variantId);
        await refreshCartUi();
      } catch (e) {
        this.setStatus(this.dataset.error || 'Unable to update gift. Please try again.', true);
        console.warn('[kc-gwp] chooseGift', e);
      } finally {
        this._syncing = false;
        this.scheduleSync();
      }
    }
  }

  if (!customElements.get('kc-gwp-picker')) {
    customElements.define('kc-gwp-picker', KcGwpPicker);
  }

  // If picker was server-rendered before this script, upgrade + sync
  document.querySelectorAll('kc-gwp-picker').forEach((el) => {
    if (el instanceof KcGwpPicker && el.isConnected) {
      el.connectedCallback();
    }
  });
})();
